import { Link, useLocation } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCredits';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  LayoutDashboard,
  Phone,
  Users,
  Bot,
  BarChart3,
  Settings,
  Headphones,
  Shield,
  Calendar,
  Target,
  UserCog,
  Code,
} from 'lucide-react';
import {
  Sidebar as SidebarComponent,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';

const UserProfile = () => {
  const { profile, loading } = useProfile();
  const { subscribed, subscription_tier, loading: creditsLoading } = useCredits();
  const { open } = useSidebar();

  if (loading) {
    return (
      <div className="border-t border-card-border p-4">
        <div className="flex items-center justify-center">
          <div className="h-8 w-8 bg-muted rounded-full animate-pulse"></div>
          {open && (
            <div className="ml-3 space-y-1">
              <div className="h-4 w-20 bg-muted rounded animate-pulse"></div>
              <div className="h-3 w-16 bg-muted rounded animate-pulse"></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase()
    : profile?.email?.[0]?.toUpperCase() || 'U';

  const displayPlan = creditsLoading 
    ? 'Loading...' 
    : subscribed 
      ? subscription_tier 
        ? subscription_tier.charAt(0).toUpperCase() + subscription_tier.slice(1) + ' Plan'
        : 'Subscribed'
      : 'Free Plan';

  return (
    <div className="border-t border-card-border p-4">
      <div className="flex items-center justify-center">
        <div className="h-8 w-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-medium text-primary">{initials}</span>
        </div>
        {open && (
          <div className="ml-3 min-w-0 flex-1">
            <p className="text-sm font-medium truncate">
              {profile?.full_name || profile?.email || 'User'}
            </p>
            <p className="text-xs text-muted-foreground">
              {displayPlan}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Campaigns', href: '/campaigns', icon: Phone },
  { name: 'Leads', href: '/leads', icon: Users },
  { name: 'Brand Intelligence', href: '/brand-intelligence', icon: Target },
  { name: 'AI Agents', href: '/agents', icon: Bot },
  { name: 'Call Logs', href: '/call-logs', icon: Headphones },
  { name: 'Calendar', href: '/calendar', icon: Calendar },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Compliance', href: '/compliance', icon: Shield },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export const Sidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      
      return !!data && !error;
    },
    enabled: !!user?.id,
  });

  return (
    <SidebarComponent 
      className="bg-card border-r border-card-border"
      collapsible="icon"
    >
      <SidebarHeader>
        <div className="flex items-center justify-center px-3 py-4">
          <img 
            src="/lovable-uploads/dee9e033-eec6-4ad6-b515-62ff1e30fdf0.png" 
            alt="Speak" 
            className="h-8 w-8 min-h-[32px] min-w-[32px] flex-shrink-0 object-contain"
          />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="sidebar-nav" data-tour="navigation-sidebar">
              {navigation.map((item, index) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                
                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <Link
                        to={item.href}
                        className="flex items-center gap-3"
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="truncate">{item.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              {/* Demo Widgets nav item hidden for now
              <SidebarMenuItem>
                <SidebarMenuButton 
                  asChild 
                  isActive={location.pathname === '/demo-widget'}
                >
                  <Link
                    to="/demo-widget"
                    className="flex items-center gap-3"
                  >
                    <Puzzle className="h-5 w-5" />
                    <span className="truncate">Demo Widgets</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              */}
              <SidebarMenuItem>
                <SidebarMenuButton 
                  asChild 
                  isActive={location.pathname === '/api-docs'}
                >
                  <Link
                    to="/api-docs"
                    className="flex items-center gap-3"
                  >
                    <Code className="h-5 w-5" />
                    <span className="truncate">API Documentation</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton 
                    asChild 
                    isActive={location.pathname === '/admin'}
                  >
                    <Link
                      to="/admin"
                      className="flex items-center gap-3"
                    >
                      <UserCog className="h-5 w-5" />
                      <span className="truncate">Admin Panel</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <UserProfile />
      </SidebarFooter>
    </SidebarComponent>
  );
};